import { RuntimeGS } from './app/RuntimeGS';
import {
    clearTemplateDocumentCache,
    getTemplateBuildId,
    getTemplateConstructor,
    registerTemplateConstructor,
    registerTemplatePack,
    templateMetadataGS,
    tryGetTemplateElement,
    unregisterTemplateConstructor
} from './template/TemplateRegistryGS';
import { WrapperGS } from './WrapperGS';

interface TemplateGSConstructor {
    new(existingElement?: HTMLElement): WrapperGS;
}

/**
 * A generated template can either create a fresh tree by name or adopt an
 * existing server-rendered root element.
 */
export class TemplateGS extends WrapperGS {
    public static get TagGSATemplateName(): string { return templateMetadataGS.templateName; }
    public static set TagGSATemplateName(value: string) { templateMetadataGS.templateName = value; }

    public static get TagGSATemplateTag(): string { return templateMetadataGS.templateTag; }
    public static set TagGSATemplateTag(value: string) { templateMetadataGS.templateTag = value; }

    public static get AttrGSControlName(): string { return templateMetadataGS.controlName; }
    public static set AttrGSControlName(value: string) { templateMetadataGS.controlName = value; }

    constructor(nameOrElement: string | HTMLElement) {
        super(nameOrElement);
    }

    /** @internal */
    public static RegisterConstructor(name: string, constructor: TemplateGSConstructor): void {
        registerTemplateConstructor(name, constructor);
    }

    /** @internal */
    public static UnregisterConstructor(name: string): void {
        unregisterTemplateConstructor(name);
    }

    /** @internal */
    public static GetConstructor(name: string): TemplateGSConstructor | undefined {
        return getTemplateConstructor<WrapperGS>(name) as TemplateGSConstructor | undefined;
    }

    public static RegisterPack(
        html: string,
        buildId?: string,
        targetDocument: Document = RuntimeGS.document
    ): void {
        registerTemplatePack(html, buildId, targetDocument);
    }

    public static async LoadPack(
        url: string,
        buildId?: string,
        fetcher: typeof fetch = fetch,
        targetDocument: Document = RuntimeGS.document
    ): Promise<void> {
        const response = await fetcher(url);
        if (!response.ok) throw new Error(`Cannot load GS template pack '${url}': HTTP ${response.status}.`);
        TemplateGS.RegisterPack(await response.text(), buildId, targetDocument);
    }

    public static async LoadPackFromDocument(
        fetcher: typeof fetch = fetch,
        targetDocument: Document = RuntimeGS.document
    ): Promise<boolean> {
        const metadata = targetDocument.querySelector('meta[name="gs-template-pack"]') as HTMLMetaElement | null;
        if (!metadata?.content) return false;
        const build = targetDocument.querySelector('meta[name="gs-build"]') as HTMLMetaElement | null;
        await TemplateGS.LoadPack(metadata.content, build?.content, fetcher, targetDocument);
        return true;
    }

    public static GetBuildId(targetDocument: Document = RuntimeGS.document): string | undefined {
        return getTemplateBuildId(targetDocument);
    }

    public static ClearDocumentCache(targetDocument: Document = RuntimeGS.document): void {
        clearTemplateDocumentCache(targetDocument);
    }

    public static GetTmpl(tmplName: string): WrapperGS<HTMLElement> {
        const wrapper = TemplateGS.TryGetTmpl(tmplName);
        if (!wrapper)
            throw new Error(`Cannot find GS template '${tmplName}'. Ensure its template pack is loaded.`);
        return wrapper;
    }

    /** @internal */
    public static TryGetTmpl(tmplName: string): WrapperGS<HTMLElement> | undefined {
        const element = tryGetTemplateElement(tmplName, WrapperGS.ClassNameTemplate);
        return element ? WrapperGS.Wrap(element) as WrapperGS<HTMLElement> : undefined;
    }
}
